import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, Navigation, HeartPulse } from "lucide-react";

const ERS = [
  { name: "St. Michael's Hospital", dist: "1.2 km", base: 18, tone: "text-[#2ee27a]", bar: "bg-[#2ee27a]" },
  { name: "Toronto General ER", dist: "2.8 km", base: 41, tone: "text-[#ffc000]", bar: "bg-[#ffc000]" },
  { name: "Toronto Western ER", dist: "4.1 km", base: 73, tone: "text-[#ff3b30]", bar: "bg-[#ff3b30]" },
];

const Ticker = ({ value, className }) => (
  <span className={`relative inline-grid overflow-hidden align-baseline ${className}`} style={{ height: "1em", lineHeight: 1 }}>
    <AnimatePresence initial={false} mode="popLayout">
      <motion.span
        key={value}
        initial={{ y: "100%", opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: "-100%", opacity: 0 }}
        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        className="block tabular-nums"
        data-testid="ticker-value"
      >
        {value}
      </motion.span>
    </AnimatePresence>
  </span>
);

const useLiveWaits = () => {
  const [waits, setWaits] = useState(ERS.map((e) => e.base));
  useEffect(() => {
    const id = setInterval(() => {
      setWaits((prev) =>
        prev.map((w, i) => {
          const drift = Math.round((Math.random() - 0.5) * 6);
          const next = w + drift;
          const lo = ERS[i].base - 6;
          const hi = ERS[i].base + 6;
          return Math.min(hi, Math.max(lo, next));
        })
      );
    }, 2800);
    return () => clearInterval(id);
  }, []);
  return waits;
};

export const PhoneMock = () => {
  const waits = useLiveWaits();
  const best = waits.indexOf(Math.min(...waits));
  return (
  <div
    data-testid="phone-mockup-frame"
    className="relative w-[280px] sm:w-[300px] aspect-[9/19] rounded-[2.6rem] bg-gradient-to-b from-[#eef2f7] to-[#acb4c0] p-[10px] shadow-[0_40px_80px_-20px_rgba(0,0,0,0.7),0_0_0_1px_rgba(255,255,255,0.12)_inset,0_0_60px_-10px_rgba(77,170,255,0.25)]"
  >
    <div className="absolute left-1/2 -translate-x-1/2 top-[14px] h-[22px] w-[92px] rounded-full bg-black z-20" />
    <div className="relative h-full w-full overflow-hidden rounded-[2.1rem] bg-[#0a1220]">
      <div className="px-5 pt-12 pb-3 flex items-center justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-[0.16em] font-semibold text-[#b7c2d4]">Nearby care</p>
          <p className="font-display text-lg font-bold text-white leading-tight">Live wait times</p>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-white/5 border border-white/10 px-2 py-1 text-[10px] font-mono text-[#e3e8f0]">
          <span className="relative h-1.5 w-1.5 rounded-full bg-[var(--green)] pulse-ring" />
          LIVE
        </span>
      </div>

      <div className="mx-4 rounded-2xl bg-[#2b3a54] border border-white/10 p-4 relative overflow-hidden">
        <span className="absolute right-4 top-4 inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#2ee27a]/15 text-[#2ee27a]">
          <HeartPulse size={18} />
        </span>
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--clinical)]/80 flex items-center gap-1.5">
          <MapPin size={10} /> Your location
        </p>
        <p className="mt-1 font-display text-3xl font-extrabold text-white leading-none" data-testid="phone-best-wait">
          <Ticker value={waits[best]} /> <span className="text-base font-semibold text-[var(--clinical)]">min</span>
        </p>
        <p className="mt-1 text-[11px] text-[#e3e8f0]">Shortest wait · {ERS[best].name.split(" ")[0]} {ERS[best].name.split(" ")[1]}</p>
      </div>

      <ul className="mt-3 px-4 space-y-2">
        {ERS.map((er, i) => (
          <motion.li
            key={er.name}
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 1.2 + i * 0.15, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="rounded-xl bg-white/5 border border-white/10 p-3"
            data-testid={`phone-er-row-${i}`}
          >
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-[12px] font-semibold text-white leading-tight">{er.name}</p>
                <p className="text-[10px] text-[#b7c2d4] flex items-center gap-1 mt-0.5">
                  <Navigation size={9} /> {er.dist}
                </p>
              </div>
              <p className={`font-display text-xl font-extrabold leading-none ${er.tone}`}>
                <Ticker value={waits[i]} />
                <span className="text-[10px] font-mono font-medium text-slate-500 ml-0.5">min</span>
              </p>
            </div>
            <div className="mt-2 h-1 rounded-full bg-white/10 overflow-hidden">
              <motion.div
                className={`h-full rounded-full ${er.bar}`}
                initial={{ width: 0 }}
                animate={{ width: `${Math.min(96, Math.round((waits[i] / 85) * 100))}%` }}
                transition={{ delay: 0.2, duration: 1, ease: [0.22, 1, 0.36, 1] }}
              />
            </div>
          </motion.li>
        ))}
      </ul>
      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 h-1 w-24 rounded-full bg-white/20" />
    </div>
  </div>
  );
};
